/**
 * Retail product performance (Analytics spec §3.7) — best/worst sellers ranked by
 * units, with revenue and margin side by side; products that never sell show with
 * 0 units (dead stock). Reads the 0149 v_product_sales view. Owner/manager
 * (route-guarded). Backend data, surfaced read-only.
 */
import { getProductSales } from '@gym/api-client';
import { useQuery } from '@tanstack/react-query';
import { Coins, Package, TrendingUp } from 'lucide-react';
import { type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ExpandableCard } from '../../components/ui/ExpandableCard.js';
import { KpiCard } from '../../components/ui/KpiCard.js';
import { Money, useFormatters } from '../../components/ui/Money.js';
import { Chip } from '../../components/ui/StatusPill.js';
import { Table, TableWrap, TD, TH, THead, TR } from '../../components/ui/Table.js';
import { useAuth } from '../../lib/auth.js';
import { supabase } from '../../lib/supabase.js';

export function ProductPerformance(): ReactNode {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const { claims } = useAuth();
  const tenantId = claims?.tenant_id ?? '';

  const products = useQuery({
    queryKey: ['an_product_sales', tenantId],
    queryFn: () => getProductSales(supabase, tenantId),
    enabled: !!tenantId,
  });

  const rows = useMemo(() => products.data ?? [], [products.data]);
  // rows arrive ranked by units_sold desc, so [0] is the best seller by volume.
  const bestByUnits = rows.length > 0 && rows[0]!.units_sold > 0 ? rows[0]! : null;
  const bestByRevenue = useMemo(
    () =>
      rows.reduce<(typeof rows)[number] | null>(
        (b, r) => (!b || r.revenue > b.revenue ? r : b),
        null,
      ),
    [rows],
  );
  const totalRevenue = useMemo(() => rows.reduce((s, r) => s + r.revenue, 0), [rows]);

  return (
    <div className="grid gap-5">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <KpiCard
          label={t('analytics.intel.bestSeller')}
          value={bestByUnits ? bestByUnits.product_name : '—'}
          icon={Package}
          tone="success"
          loading={products.isLoading}
          hint={
            bestByUnits
              ? t('analytics.intel.unitsValue', { n: fmt.number(bestByUnits.units_sold) })
              : undefined
          }
        />
        <KpiCard
          label={t('analytics.intel.topRevenueProduct')}
          value={bestByRevenue && bestByRevenue.revenue > 0 ? bestByRevenue.product_name : '—'}
          icon={TrendingUp}
          tone="primary"
          loading={products.isLoading}
          hint={
            bestByRevenue && bestByRevenue.revenue > 0
              ? fmt.money(bestByRevenue.revenue)
              : undefined
          }
        />
        <KpiCard
          label={t('analytics.intel.retailRevenue')}
          value={<Money minor={totalRevenue} className="text-3xl" />}
          icon={Coins}
          tone="info"
          loading={products.isLoading}
        />
      </div>

      <ExpandableCard
        title={t('analytics.intel.productSales')}
        subtitle={t('analytics.intel.productSalesSub')}
        loading={products.isLoading}
        empty={rows.length === 0 ? t('analytics.intel.noProductSales') : undefined}
      >
        <TableWrap>
          <Table>
            <THead>
              <tr>
                <TH>{t('analytics.intel.product')}</TH>
                <TH>{t('analytics.category')}</TH>
                <TH numeric>{t('analytics.intel.unitsSold')}</TH>
                <TH numeric>{t('analytics.intel.revenue')}</TH>
                <TH numeric>{t('analytics.intel.marginCol')}</TH>
              </tr>
            </THead>
            <tbody>
              {rows.map((r) => (
                <TR key={r.product_id}>
                  <TD className="font-medium">{r.product_name}</TD>
                  <TD className="text-muted">{r.category ?? '—'}</TD>
                  <TD numeric>
                    <Chip tone={r.units_sold > 0 ? 'neutral' : 'warning'}>
                      {fmt.number(r.units_sold)}
                    </Chip>
                  </TD>
                  <TD numeric>
                    <Money minor={r.revenue} />
                  </TD>
                  <TD numeric>
                    {r.margin_pct != null ? (
                      <Chip
                        tone={
                          r.margin_pct >= 40 ? 'success' : r.margin_pct >= 20 ? 'warning' : 'danger'
                        }
                      >
                        {r.margin_pct}%
                      </Chip>
                    ) : (
                      '—'
                    )}
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      </ExpandableCard>
    </div>
  );
}
